import { components } from "@apiSchema";
import { iPermissionFields, PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";
import { ID } from "@models/index";
import { iFoodProfileRecommendedCharts } from "@models/FoodProfileRecommendedCharts";
import { routeNames } from "@core/routeNames";
import { prepareForMatching } from "@helpers";

type API_Model = components["schemas"]["MacAndMenuTrendDataViewModelChainPermissionWrapper"];

export interface IRecommendedChart {
    id: ID;
    title: string;
    typeLabel: RecommendedChartTypeEnum;
    link: string;
    downloadOptions?: Array<{
        title: string;
        url: string;
    }>
}

export enum RecommendedChartTypeEnum {
    Mac = "MAC",
    InstantChart = "Instant Chart",
    CategoryReport = "Category Report",
}

export interface IRecommendedCharts extends iPermissionFields {
    data: IRecommendedChart[];
}

interface IData {
    apiModel?: API_Model;
    searchQuery: string;
    randomize?: boolean;
}

export class RecommendedCharts implements IRecommendedCharts {
    static readonly defaultData: IRecommendedCharts = {
        permissionLevel: PermissionLevelEnum.None,
        hasData: false,
        data: [],
    };

    permissionLevel = RecommendedCharts.defaultData.permissionLevel;
    hasData = RecommendedCharts.defaultData.hasData;
    data = RecommendedCharts.defaultData.data;

    constructor(data?: IData) {
        if (data && data.apiModel) {
            this.mapFromApi(data.apiModel, data.searchQuery, data.randomize);
        }
    }

    private setData(model: iFoodProfileRecommendedCharts) {
        ({
            permissionLevel: this.permissionLevel,
            hasData: this.hasData,
            data: this.data,
        } = model);
    }

    private mapFromApi(apiModel: API_Model, searchQuery: string, randomize = false) {
        if (apiModel.data) {
            const macItems = apiModel.data.mac?.items ?? [];
            const instantChartsItems = apiModel.data.instantChart?.items ?? [];
            const categoryReports = apiModel.data.categoryOverview?.items ?? [];

            const allItems: IRecommendedChart[] = [
                ...macItems.map(i => ({
                    id: i.id ?? 0,
                    title: i.name ?? '',
                    typeLabel: RecommendedChartTypeEnum.Mac,
                    link: `${routeNames.viewDocument}?id=${i.id}&doctype=3&fromDocuments=${encodeURIComponent(searchQuery)}`,
                })),
                ...instantChartsItems.map(i => ({
                    id: i.id ?? 0,
                    title: i.name ?? '',
                    typeLabel: RecommendedChartTypeEnum.InstantChart,
                    link: `${routeNames.instantCharts}?q=${encodeURIComponent(searchQuery)}&id=${i.id}`,
                })),
                ...categoryReports.map(i => ({
                    id: i.id ?? 0,
                    title: i.name ?? '',
                    typeLabel: RecommendedChartTypeEnum.CategoryReport,
                    link: '',
                    downloadOptions: [
                        { title: "PDF", url: i.pdfExportLink, },
                        { title: "PPT", url: i.pptExportLink, },
                    ],
                })),
            ];

            if (randomize) {
                allItems.sort(() => Math.random() - 0.5);
            }
            else {
                allItems.sort(({ title: a }, { title: b }) => {
                    return prepareForMatching(a) < prepareForMatching(b) ? -1 : 1;
                });
            }

            this.setData({
                permissionLevel: PermissionSection.getPermissionLevel(apiModel.permissionLevel),
                hasData: apiModel.hasData ?? allItems.length > 0,
                data: allItems,
            });
        }
    }
}